import ConfirmDialog from '../components/ui/ConfirmDialog';
import PageHeader from '../components/ui/PageHeader';
import { userFacingError } from '../utils/userFacingError';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { KIAuthorsService } from '../services/kiAuthorsService';
import { KIAuthorAutomaticRun, KIAuthorCharacter, KIAuthorWebhookResult, KIAuthorsConfig } from '../types';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import useUnsavedChanges from '../hooks/useUnsavedChanges';

const HHMM_REGEX = /^([01]\d|2[0-3]):([0-5]\d)$/;
const configSnapshot = (config: KIAuthorsConfig) => JSON.stringify([
  config.autoPublishEnabled, config.autoPublishTime, config.webhookUrl, config.commonSystemPrompt,
  config.feeds.map(feed => [feed.url, feed.outlet]),
  config.characters.map(author => [author.name, author.character, author.stylePrompt, author.time, author.enabled])
]);
const scheduleDescription = (value: KIAuthorsConfig) => value.autoPublishEnabled
  ? `Täglich um ${value.autoPublishTime} Uhr · deutsche Ortszeit`
  : 'Automatische Veröffentlichung ausgeschaltet';
const buttonClass = 'inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

const KIAuthorsPage: React.FC = () => {
  const { user, hasRole } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const [config, setConfig] = useState<KIAuthorsConfig | null>(null);
  const [savedConfig, setSavedConfig] = useState<KIAuthorsConfig | null>(null);
  const [results, setResults] = useState<KIAuthorWebhookResult[]>([]);
  const [loadError, setLoadError] = useState(false);
  const [lastRun, setLastRun] = useState<KIAuthorAutomaticRun | null>(null);
  const [runStatusUnavailable, setRunStatusUnavailable] = useState(false);
  const isAllowed = hasRole(['administrator', 'redakteur']);
  const isDirty = !!config && !!savedConfig && configSnapshot(config) !== configSnapshot(savedConfig);
  const blocker = useUnsavedChanges(isDirty || isSaving || isRequesting, false, isSaving || isRequesting);

  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    if (!isDirty && !isSaving && !isRequesting) blocker.proceed();
  }, [blocker, isDirty, isSaving, isRequesting]);

  const refreshRun = useCallback(async () => {
    try {
      setLastRun(await KIAuthorsService.getLatestAutomaticRun());
      setRunStatusUnavailable(false);
    } catch { setRunStatusUnavailable(true); }
  }, []);

  useEffect(() => {
    if (!isAllowed) { setIsLoading(false); return; }
    const load = async () => {
      try {
        const loaded = await KIAuthorsService.getConfig();
        setConfig(loaded);
        setSavedConfig(loaded);
        await refreshRun();
      } catch {
        setLoadError(true);
        toast.error('Konfiguration konnte nicht geladen werden');
      } finally { setIsLoading(false); }
    };
    load();
  }, [isAllowed, refreshRun]);

  const isValid = useMemo(() => {
    if (!config || !HHMM_REGEX.test(config.autoPublishTime)) return false;
    if (config.autoPublishEnabled && !config.characters.some(c => c.enabled)) return false;
    if (!config.webhookUrl.trim() || !config.commonSystemPrompt.trim()) return false;
    if (!config.feeds.length || config.feeds.some(f => !f.url.trim() || !f.outlet.trim())) return false;
    if (config.characters.length !== 5) return false;
    return !config.characters.some(c => !c.name.trim() || !c.character.trim() || !c.stylePrompt.trim() || !HHMM_REGEX.test(c.time));
  }, [config]);
  const selectedAuthorCount = config?.characters.filter(author => author.enabled).length || 0;
  const savedAuthorCount = savedConfig?.characters.filter(author => author.enabled).length || 0;
  const busy = isSaving || isRequesting;

  const updateCharacter = (index: number, patch: Partial<KIAuthorCharacter>) => {
    if (!config) return;
    setConfig({ ...config, characters: config.characters.map((author, i) => i === index ? { ...author, ...patch } : author) });
  };

  const save = async () => {
    if (!config || !user || !isDirty || !isValid || busy) return;
    try {
      setIsSaving(true);
      await KIAuthorsService.saveConfig(config, user.id);
      const stored = await KIAuthorsService.getConfig();
      if (configSnapshot(stored) !== configSnapshot(config)) throw new Error('Gespeicherte Einstellungen weichen ab');
      setConfig(stored);
      setSavedConfig(stored);
      toast.success('Alle Einstellungen gespeichert');
    } catch {
      toast.error('Speichern konnte nicht bestätigt werden. Deine Änderungen bleiben erhalten.');
    } finally { setIsSaving(false); }
  };

  const runWebhook = async () => {
    // Manuelle und automatische Aufträge verwenden dieselbe gespeicherte Konfiguration.
    if (!savedConfig || isDirty || !isValid || !selectedAuthorCount || busy) return;
    try {
      setIsRequesting(true);
      setResults([]);
      setResults(await KIAuthorsService.requestArticles(savedConfig));
      toast.success('Autoren erfolgreich beauftragt');
    } catch (error) {
      toast.error(`Auftrag fehlgeschlagen: ${userFacingError(error, 'Unbekannter Fehler')}`);
    } finally { setIsRequesting(false); }
  };

  if (!isAllowed) return <div className="card"><PageHeader title="KI-Autoren" description="Du hast keine Berechtigung für dieses Werkzeug." /></div>;
  if (loadError) return <div className="card text-red-700" role="alert">Die Einstellungen konnten nicht geladen werden. Bitte lade die Seite erneut.</div>;
  if (isLoading || !config || !savedConfig) return <div className="card flex justify-center p-8"><LoadingSpinner size="lg" /></div>;

  return (
    <div className="space-y-6 pb-8">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <PageHeader title="KI-Autoren" description="Autoren auswählen, Veröffentlichung planen und Schreibaufträge für freiheitsfunken.info steuern." />
        </div>
        <div className="flex flex-wrap gap-2 text-xs font-medium">
          <span className="rounded-full bg-blue-100 px-3 py-1.5 text-blue-800">{savedAuthorCount} von 5 Autoren aktiv</span>
          <span className={`rounded-full px-3 py-1.5 ${savedConfig.autoPublishEnabled ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'}`}>
            {savedConfig.autoPublishEnabled ? `Automatik · ${savedConfig.autoPublishTime} Uhr` : 'Automatik aus'}
          </span>
        </div>
      </header>

      <div className={`sticky top-20 z-20 flex flex-wrap items-center justify-between gap-3 rounded-lg border p-4 shadow-sm ${isDirty ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-white'}`}>
        <div role="status" aria-live="polite">
          <p className={`text-sm font-semibold ${isDirty ? 'text-amber-900' : 'text-gray-800'}`}>
            {isSaving ? 'Einstellungen werden gespeichert …' : isDirty ? 'Ungespeicherte Änderungen' : 'Alle Änderungen gespeichert'}
          </p>
          <p className="mt-0.5 text-xs text-gray-600">{isDirty ? 'Die Automatik verwendet bis zum Speichern die bisherigen Einstellungen.' : 'Autorenauswahl, Zeiten, Prompts und Quellen sind auf dem aktuellen Stand.'}</p>
          {isDirty && !isValid && <p className="mt-1 text-xs text-red-700">Bitte alle Pflichtfelder und Uhrzeiten prüfen; für die Automatik mindestens einen Autor auswählen.</p>}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {isDirty && <button type="button" onClick={() => setConfig(savedConfig)} disabled={busy} className="text-sm text-gray-700 hover:underline disabled:opacity-50">Änderungen verwerfen</button>}
          <button type="button" onClick={save} disabled={!isDirty || !isValid || busy}
            className={`${buttonClass} bg-green-600 text-white hover:bg-green-700 focus:ring-green-500`}>
            {isSaving ? 'Speichert …' : 'Änderungen speichern'}
          </button>
        </div>
      </div>

      <fieldset disabled={busy} className="min-w-0 space-y-6">
        <section className="rounded-lg border border-gray-200 bg-white shadow-sm" aria-labelledby="authors-heading">
          <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-200 px-6 py-4">
            <div><h2 id="authors-heading" className="text-lg font-semibold text-gray-900">Autoren & Veröffentlichungszeiten</h2>
              <p className="mt-1 text-sm text-gray-500">Pro ausgewähltem Autor entsteht ein Artikel. Stil und Profil lassen sich einzeln bearbeiten.</p></div>
            <span className="rounded-full bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700">{selectedAuthorCount} ausgewählt</span>
          </div>
          <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2 xl:grid-cols-3">
            {config.characters.map((author, index) => (
              <div key={index} className={`rounded-lg border p-4 ${author.enabled ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-gray-50'}`}>
                <label className="flex cursor-pointer items-start gap-3">
                  <input type="checkbox" checked={author.enabled} onChange={e => updateCharacter(index, { enabled: e.target.checked })}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                  <span><span className="block text-sm font-semibold text-gray-900">{author.character || `Autor ${index + 1}`}</span>
                    <span className="block text-xs text-gray-500">{author.name || 'Profilname fehlt'}</span></span>
                </label>
                <label className="mt-4 block text-xs font-medium text-gray-700">Veröffentlichung um
                  <input type="time" aria-label={`Veröffentlichungszeit für ${author.character || `Autor ${index + 1}`}`} value={author.time} onChange={e => updateCharacter(index, { time: e.target.value })} className="input-field mt-1" required />
                </label>
                <details className="mt-4 border-t border-gray-200 pt-3">
                  <summary className="cursor-pointer text-sm font-medium text-blue-700">Stil und Profil bearbeiten</summary>
                  <div className="mt-3 space-y-3">
                    <label className="block text-xs font-medium text-gray-700">Profilname
                      <input type="text" value={author.name} onChange={e => updateCharacter(index, { name: e.target.value })} className="input-field mt-1" required /></label>
                    <label className="block text-xs font-medium text-gray-700">Autorenname
                      <input type="text" value={author.character} onChange={e => updateCharacter(index, { character: e.target.value })} className="input-field mt-1" required /></label>
                    <label className="block text-xs font-medium text-gray-700">Stil-Prompt
                      <textarea value={author.stylePrompt} onChange={e => updateCharacter(index, { stylePrompt: e.target.value })} className="input-field mt-1 min-h-[180px]" required /></label>
                  </div>
                </details>
              </div>
            ))}
          </div>
        </section>

        <section aria-labelledby="publishing-heading">
          <h2 id="publishing-heading" className="mb-3 text-lg font-semibold text-gray-900">Beauftragung & Veröffentlichung</h2>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
              <h3 className="font-semibold text-gray-900">Tägliche Automatik</h3>
              <p className="mt-1 text-sm text-gray-500">Die gespeicherten Autoren werden täglich ohne geöffneten Browser beauftragt.</p>
              <label className="mt-4 flex items-center gap-2 text-sm font-medium text-gray-800">
                <input type="checkbox" checked={config.autoPublishEnabled} onChange={e => setConfig({ ...config, autoPublishEnabled: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                Automatisch beauftragen und veröffentlichen
              </label>
              <label className="mt-4 block max-w-xs text-sm font-medium text-gray-700">Täglicher Start (deutsche Ortszeit)
                <input type="time" value={config.autoPublishTime} onChange={e => setConfig({ ...config, autoPublishTime: e.target.value })} className="input-field mt-1" required /></label>
              <p className="mt-2 text-xs text-gray-500">Sommer- und Winterzeit werden berücksichtigt. Die Veröffentlichungszeiten der Autoren gelten weiterhin.</p>
              <div className="mt-4 rounded-md bg-gray-50 p-3 text-sm text-gray-700">
                <p className="text-xs text-gray-500">Gespeicherter Zeitplan</p>
                <p className="mt-1 font-medium">{scheduleDescription(savedConfig)}</p>
                <p className="mt-1 text-xs text-gray-500">Höchstens ein automatischer Auftrag pro Tag.</p>
              </div>
              <div className="mt-4 border-t border-gray-200 pt-3 text-sm text-gray-600" aria-live="polite">
                {runStatusUnavailable ? 'Status konnte nicht geladen werden.' : lastRun ? <>
                  <p className="font-medium text-gray-800">Letzter automatischer Auftrag</p>
                  <p className="mt-1">{new Date(lastRun.startedAt).toLocaleString('de-DE', { timeZone: 'Europe/Berlin' })} Uhr · {lastRun.authorCount} Autoren · {lastRun.status === 'completed' ? 'an n8n übergeben' : lastRun.status === 'failed' ? 'fehlgeschlagen' : 'gestartet'}</p>
                  {lastRun.status === 'failed' && <p className="mt-1 text-red-700">{userFacingError(lastRun.error, 'Der automatische Auftrag ist fehlgeschlagen.')} Bitte vor einem Neuauftrag die n8n-Ausführung prüfen.</p>}
                  {lastRun.status === 'running' && <p className="mt-1">Bei einem länger ausstehenden Ergebnis bitte die n8n-Ausführung prüfen.</p>}
                </> : <p>Noch kein automatischer Auftrag ausgeführt.</p>}
                <button type="button" onClick={refreshRun} className="mt-2 text-sm text-blue-700 hover:underline">Status aktualisieren</button>
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
              <h3 className="font-semibold text-gray-900">Manuell beauftragen</h3>
              <p className="mt-1 text-sm text-gray-500">Einen Schreibauftrag mit der gespeicherten Auswahl starten.</p>
              <p className="mt-4 text-3xl font-semibold text-gray-900">{savedAuthorCount}<span className="ml-2 text-sm font-normal text-gray-500">Artikel pro Auftrag</span></p>
              <p className="mt-2 text-sm text-gray-600">{savedConfig.characters.filter(author => author.enabled).map(author => author.character).join(', ') || 'Keine Autoren ausgewählt'}</p>
              {savedConfig.autoPublishEnabled && <p className="mt-4 rounded-md bg-amber-50 p-3 text-sm text-amber-800">Ein manueller Auftrag erzeugt zusätzliche Artikel zum automatischen Tagesauftrag.</p>}
              {isDirty && <p className="mt-4 text-sm text-amber-800">Bitte zuerst deine Änderungen speichern oder verwerfen.</p>}
              <button type="button" onClick={runWebhook} disabled={!isValid || isDirty || !selectedAuthorCount || busy}
                className={`${buttonClass} mt-4 bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500`}>
                {isRequesting ? 'Autoren schreiben …' : `KI-Autoren beauftragen (${savedAuthorCount})`}
              </button>
              <p className="mt-3 text-xs text-gray-500">Die Erstellung kann etwa eine Minute dauern. Die Ergebnisse erscheinen anschließend unten.</p>
            </div>
          </div>
        </section>

        <section className="rounded-lg border border-gray-200 bg-white shadow-sm" aria-labelledby="settings-heading">
          <div className="border-b border-gray-200 px-6 py-4"><h2 id="settings-heading" className="text-lg font-semibold text-gray-900">Schreibauftrag & Quellen</h2>
            <p className="mt-1 text-sm text-gray-500">Gemeinsame Vorgaben und Themenquellen für alle ausgewählten Autoren.</p></div>
          <div className="divide-y divide-gray-200 px-6">
            <details className="py-4">
              <summary className="cursor-pointer text-sm font-semibold text-gray-800">Gemeinsamer Prompt</summary>
              <label className="mt-3 block text-sm text-gray-600">Schreibvorgaben für alle Autoren
                <textarea value={config.commonSystemPrompt} onChange={e => setConfig({ ...config, commonSystemPrompt: e.target.value })} className="input-field mt-2 min-h-[240px]" required /></label>
            </details>
            <details className="py-4">
              <summary className="cursor-pointer text-sm font-semibold text-gray-800">RSS-Feeds · {config.feeds.length} Quellen</summary>
              <div className="mt-3 space-y-3">
                <p className="text-sm text-gray-500">Feeds liefern Themen; die Artikelzahl richtet sich nach der Autorenauswahl.</p>
                {config.feeds.map((feed, index) => (
                  <div key={index} className="grid grid-cols-1 items-end gap-3 rounded-md bg-gray-50 p-3 md:grid-cols-12">
                    <label className="text-xs font-medium text-gray-700 md:col-span-3">Quelle
                      <input type="text" value={feed.outlet} onChange={e => setConfig({ ...config, feeds: config.feeds.map((f, i) => i === index ? { ...f, outlet: e.target.value } : f) })} className="input-field mt-1" required /></label>
                    <label className="text-xs font-medium text-gray-700 md:col-span-7">Feed-URL
                      <input type="url" value={feed.url} onChange={e => setConfig({ ...config, feeds: config.feeds.map((f, i) => i === index ? { ...f, url: e.target.value } : f) })} className="input-field mt-1" required /></label>
                    <button type="button" aria-label={`Quelle ${feed.outlet || index + 1} entfernen`} onClick={() => setConfig({ ...config, feeds: config.feeds.filter((_, i) => i !== index) })}
                      className="rounded-md px-3 py-2 text-sm text-red-600 hover:bg-red-50 md:col-span-2">Entfernen</button>
                  </div>
                ))}
                <button type="button" onClick={() => setConfig({ ...config, feeds: [...config.feeds, { url: '', outlet: '' }] })} className={`${buttonClass} border border-gray-300 text-gray-700 hover:bg-gray-50`}>Feed hinzufügen</button>
              </div>
            </details>
            <details className="py-4">
              <summary className="cursor-pointer text-sm font-semibold text-gray-800">Verbindung zu n8n</summary>
              <label className="mt-3 block text-sm text-gray-600">Webhook-URL
                <input type="url" value={config.webhookUrl} onChange={e => setConfig({ ...config, webhookUrl: e.target.value })} className="input-field mt-2" required /></label>
              <p className="mt-2 text-xs text-gray-500">Bei einem neuen Webhook muss auch das erlaubte Ziel der Automatik aktualisiert werden.</p>
            </details>
          </div>
        </section>
      </fieldset>

      {(results.length > 0 || isRequesting) && <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm" aria-labelledby="results-heading">
        <h2 id="results-heading" className="mb-4 text-lg font-semibold text-gray-900">Ergebnisse des manuellen Auftrags</h2>
        {isRequesting ? <div role="status" className="flex items-center gap-3 text-sm text-gray-600"><LoadingSpinner size="sm" />Die ausgewählten Autoren schreiben …</div> : <div className="space-y-4">
          {results.map((item, index) => <article key={`${item.id}-${index}`} className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <div className="mb-2 flex flex-wrap gap-2 text-xs"><span className="rounded-full bg-blue-100 px-2 py-1 text-blue-800">{item.author}</span><span className="rounded-full bg-gray-200 px-2 py-1 text-gray-700">{item.outlet}</span></div>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">{item.title}</h3>
            <a href={item.original_url} target="_blank" rel="noreferrer" className="break-all text-sm text-blue-600 hover:underline">{item.original_url}</a>
            <div className="mt-3 whitespace-pre-wrap text-sm leading-6 text-gray-800">{item.commentary}</div>
          </article>)}
        </div>}
      </section>}

      <ConfirmDialog open={blocker.state === 'blocked'} title="Ungespeicherte Änderungen"
        description={isSaving || isRequesting ? 'Bitte warte, bis der laufende Vorgang abgeschlossen ist.' : 'Beim Verlassen gehen deine Änderungen verloren. Die gespeicherten Einstellungen bleiben gültig.'}
        cancelLabel="Auf Seite bleiben" confirmLabel="Verwerfen und verlassen" danger confirmDisabled={isSaving || isRequesting}
        onClose={() => { if (blocker.state === 'blocked') blocker.reset(); }}
        onConfirm={() => { if (blocker.state === 'blocked') blocker.proceed(); }} />

    </div>
  );
};

export default KIAuthorsPage;
